import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
  // PayloadAction,
  // createAction,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import { Meta } from "admin/types/meta";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { AdminUser } from "admin/types/adminUser";
import { RootState } from "admin/store";
import { API_BASE_PATH } from "../../constants/constants";

const key = "adminUser";
const adapter = createEntityAdapter<AdminUser.AdminUser>();

const initialStateValues: AdminUser.AdminUserState = {
  adminUser: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchAdminUsers = createAsyncThunk<
  {
    adminUsers: AdminUser.AdminUser[];
    meta: Meta;
  },
  {
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchAdminUsers`, async (args = {}, { rejectWithValue }) => {
  try {
    const { param, isServerSide = false, headers, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/manager/admin_users.json?${param}`,
      {
        headers,
      },
    );
    return {
      adminUsers: result.data.adminUsers,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postAdminUser = createAsyncThunk<
  {
    adminUser: AdminUser.AdminUser;
  },
  { params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { params } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/manager/admin_users.json`,
      {
        admin_user: params,
      },
    );
    console.log(result);
    return {
      adminUser: result.data.adminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchAdminUser = createAsyncThunk<
  {
    adminUser: AdminUser.AdminUser;
  },
  {
    id: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { id, isServerSide = false, headers, locale } = args;
    const result = await createWrapper({ isServerSide, locale }).get(
      `${API_BASE_PATH}/manager/admin_users/${id}.json`,
      { headers },
    );
    console.log(result);
    return {
      adminUser: result.data.adminUser,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putAdminUser = createAsyncThunk<
  {
    adminUser: AdminUser.AdminUser;
  },
  { id: number; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putAdminUser`, async (args, { rejectWithValue }) => {
  try {
    const { id, params } = args;
    const result = await createWrapper().put(
      `${API_BASE_PATH}/manager/admin_users/${id}.json`,
      { admin_user: params },
    );
    console.log(result);
    return {
      adminUser: result.data.adminUser,
    };
  } catch (err) {
    console.log(err.response);
    return rejectWithValue(err.data.errors);
  }
});

export const resendAdminUserInvitation = createAsyncThunk<
  void,
  { id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/resendAdminUserInvitation`, async (args, { rejectWithValue }) => {
  try {
    const { id } = args;
    const result = await createWrapper().post(
      `${API_BASE_PATH}/manager/admin_users/${id}/resend_invitation_mails.json`,
    );
    console.log(result);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const adminUserSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setAdminUser: (state, action) => {
      state.adminUser = action.payload;
    },
    setAdminUsers: (state, action) => {
      adapter.setAll(state, action.payload.adminUsers);
      state.meta = action.payload.meta;
    },
    clearAdminUser: (state) => {
      state.adminUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.adminUser };
      })
      .addCase(postAdminUser.fulfilled, (state, action) => {
        state.adminUser = action.payload.adminUser;
      })
      .addCase(fetchAdminUsers.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.adminUsers);
        state.meta = action.payload.meta;
      })
      .addCase(fetchAdminUser.pending, (state, action) => {
        const targetUserId = action.meta.arg.id;
        if (state.adminUser && state.adminUser.id !== targetUserId) {
          // 別のユーザーデータを取得するため選択中のユーザーをクリアする
          state.adminUser = null;
        }
      })
      .addCase(fetchAdminUser.fulfilled, (state, action) => {
        state.adminUser = action.payload.adminUser;
      })
      .addCase(putAdminUser.fulfilled, (state, action) => {
        state.adminUser = action.payload.adminUser;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: adminUserByIdSelector,
  selectIds: adminUserIdsSelector,
  selectEntities: adminUserEntitiesSelector,
  selectAll: adminUsersSelector,
  selectTotal: totalAdminUsersSelector,
} = adapter.getSelectors((state: RootState) => state.adminUser);

const stateSelector = (state: RootState) => state[key];

export const adminUserSelector = createSelector(
  stateSelector,
  (state) => state.adminUser,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const adminUsersMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

/** action export **/
export const { setAdminUser, setAdminUsers, clearAdminUser } =
  adminUserSlice.actions;
